import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  // InputAdornment,
  Step,
  StepLabel,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
// import { LocationOn } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import heroImage from '../../../assets/facility/signup-background.jpg';
import Logo from '../../../assets/main/logo-white.png';
import {
  PageContainer,
  HeroPanel,
  HeroImage,
  HeroBranding,
  FormPanel,
  FormContainer,
  StyledStepper,
  RequiredAsterisk,
  StyledTextField,
  StyledFormControl,
  SubmitButton,
  FormBox,
  ErrorMessage,
} from './styles';
import { facilitySignup, fetchFacilityTypes } from '../../../modules/facility/actions';
import {
  selectFacilityTypesList,
  selectFacilityTypesLoading,
  selectFacilityTypesError,
  selectFacilitySignupLoading
} from '../../../modules/facility/selectors';
import { AppDispatch } from '../../../store';
import { IFacilitySignupPayload } from '../../../modules/facility/types';
import AlertPopup from '../../../components/common/AlertPopup';
import GoogleMapLocationPicker from '../../../components/GoogleMapLocationPicker';
import { GOOGLE_MAPS_API_KEY } from '../../../utils/env';
import { LocationValue } from '../../../components/GoogleMapLocationPicker';


const schema = yup.object().shape({
  facilityName: yup.string().required('Facility name is required'),
  type: yup.string().required('Facility type is required'),
  contactPerson: yup.string().required('Contact person is required'),
  designation: yup.string().required('Designation is required'),
  phoneNumber: yup.string().required('Phone number is required').matches(/^\d+$/, 'Only digits are allowed'),
  emailAddress: yup.string().email('Invalid email').required('Email is required'),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  locationName: yup.string().required('Location name is required'),
  abnNumber: yup.string().required('ABN number is required'),
});

const steps = ['Basic Details', 'Verification Documents'];

const FacilitySignupFirstPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const orgCode = searchParams.get('org_code') || '';
  const [activeStep, setActiveStep] = useState<number>(0);
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);
  const [latitude, setLatitude] = useState<string>('');
  const [longitude, setLongitude] = useState<string>('');
  const [locationName, setLocationName] = useState<string>('');


  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    register('locationName');
  }, [register]);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const isLoading = useSelector(selectFacilitySignupLoading);

  const facilityTypes = useSelector(selectFacilityTypesList);
  const isLoadingFacilityTypes = useSelector(selectFacilityTypesLoading);
  const errorFacilityTypes = useSelector(selectFacilityTypesError);

  useEffect(() => {
    dispatch(fetchFacilityTypes({ page: 1, page_size: 100 }));
  }, [dispatch]);

  const handleLocationSelect = (v: LocationValue) => {
    setLatitude(v.lat.toString());
    setLongitude(v.lng.toString());
    setLocationName(v.location_name);
    setValue('locationName', v.location_name);
    trigger('locationName');
  };

  const handleNext = async (data: any): Promise<void> => {
    const payload: IFacilitySignupPayload = {
      ...(orgCode ? { org_code: orgCode } : { org_id: 1 }),
      email: data.emailAddress,
      first_name: data.firstName,
      last_name: data.lastName,
      phone_number: data.phoneNumber,
      address: data.locationName,
      facility_name: data.facilityName,
      latitude: latitude,
      longitude: longitude,
      location_name: data.locationName,
      facility_type_id: parseInt(data.type, 10),
      contact_person: data.contactPerson,
      contact_person_designation: data.designation,
      user_type: 'facility',
      abn_number: data.abnNumber,
      password: Math.random().toString(36).slice(-8), // Temporary password
    };
    try {
      const resultAction = await dispatch(facilitySignup(payload));
      unwrapResult(resultAction);
      setAlert({ message: 'Facility signup started, continuing for the required certificates uploads!', severity: 'success' });
      setTimeout(() => {
        navigate('/facility/signup/file-upload');
      }, 2000);
    } catch (err: any) {
      setAlert({ message: err || 'Failed to sign up.', severity: 'error' });
    }
  };

  const handleBack = (): void => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  return (
    <PageContainer>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      {/* Left Panel - Hero Image */}
      <HeroPanel>
        <HeroImage src={heroImage} alt="Healthcare professional" />
        <HeroBranding>
          <Box>
            <img src={Logo} alt="Facility signup logo" />
          </Box>
        </HeroBranding>
      </HeroPanel>

      {/* Right Panel - Form */}
      <FormPanel>
        <FormContainer>
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" gutterBottom>
              Register Your Facility
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create your account to post shifts, manage workers, and streamline your facility's staffing process
            </Typography>
          </Box>

          {/* Stepper */}
          <StyledStepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </StyledStepper>

          {/* Form */}
          {activeStep === 0 && (
            <FormBox onSubmit={handleSubmit(handleNext)}>

              <Box mb={3}>
                <InputLabel htmlFor="facilityName" sx={{ mb: 1, fontWeight: 500 }}>
                  Facility Name<RequiredAsterisk>*</RequiredAsterisk>
                </InputLabel>
                <StyledTextField
                  fullWidth
                  id="facilityName"
                  placeholder="Enter facility legal name"
                  {...register('facilityName')}
                  error={!!errors.facilityName}
                  variant="outlined"
                />
                {errors.facilityName && <ErrorMessage>{errors.facilityName.message}</ErrorMessage>}
              </Box>

              <Box mb={3}>
                <InputLabel htmlFor="firstName" sx={{ mb: 1, fontWeight: 500 }}>
                  First Name<RequiredAsterisk>*</RequiredAsterisk>
                </InputLabel>
                <StyledTextField
                  fullWidth
                  id="firstName"
                  placeholder="Enter your first name"
                  {...register('firstName')}
                  error={!!errors.firstName}
                  variant="outlined"
                />
                {errors.firstName && <ErrorMessage>{errors.firstName.message}</ErrorMessage>}
              </Box>

              <Box mb={3}>
                <InputLabel htmlFor="lastName" sx={{ mb: 1, fontWeight: 500 }}>
                  Last Name<RequiredAsterisk>*</RequiredAsterisk>
                </InputLabel>
                <StyledTextField
                  fullWidth
                  id="lastName"
                  placeholder="Enter your last name"
                  {...register('lastName')}
                  error={!!errors.lastName}
                  variant="outlined"
                />
                {errors.lastName && <ErrorMessage>{errors.lastName.message}</ErrorMessage>}
              </Box>

              <Box mb={3}>
                <InputLabel htmlFor="abnNumber" sx={{ mb: 1, fontWeight: 500 }}>
                  ABN Number<RequiredAsterisk>*</RequiredAsterisk>
                </InputLabel>
                <StyledTextField
                  fullWidth
                  id="abnNumber"
                  placeholder="Enter your ABN number"
                  {...register('abnNumber')}
                  error={!!errors.abnNumber}
                  variant="outlined"
                />
                {errors.abnNumber && <ErrorMessage>{errors.abnNumber.message}</ErrorMessage>}
              </Box>

              <Box mb={3}>
                <InputLabel htmlFor="type" sx={{ mb: 1, fontWeight: 500 }}>
                  Type<RequiredAsterisk>*</RequiredAsterisk>
                </InputLabel>
                <StyledFormControl fullWidth disabled={isLoadingFacilityTypes} error={!!errors.type}>
                  <Controller
                    name="type"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        id="type" 
                        displayEmpty
                        MenuProps={{
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                          PaperProps: {
                            sx: {
                              mt: 0.5,
                              borderRadius: '8px',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                              // Force the popup to align to the right edge of its anchor
                              '& .MuiMenuItem-root': {
                                py: 1.5,
                                px: 2,
                                fontSize: '0.95rem',
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="" disabled sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          {isLoadingFacilityTypes ? 'Loading...' : '-Select facility type-'}
                        </MenuItem>
                        {facilityTypes && facilityTypes.map((type: any) => (
                          <MenuItem key={type.facility_type_id} value={type.facility_type_id}>
                            {type.facility_type_name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </StyledFormControl>
                {errors.type && <ErrorMessage>{errors.type.message}</ErrorMessage>}
                {errorFacilityTypes && <Alert severity="error">{errorFacilityTypes}</Alert>}
              </Box>



              <Box mb={3}>
                <InputLabel htmlFor="locationName" sx={{ mb: 1, fontWeight: 500 }}>
                  Location Name<RequiredAsterisk>*</RequiredAsterisk>
                </InputLabel>
                <GoogleMapLocationPicker
                  apiKey={GOOGLE_MAPS_API_KEY}
                  value={latitude && longitude ? { lat: parseFloat(latitude), lng: parseFloat(longitude), location_name: locationName } : null}
                  onChange={handleLocationSelect}
                  size='medium'
                />
                {errors.locationName && <ErrorMessage>{errors.locationName.message}</ErrorMessage>}
              </Box>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <InputLabel htmlFor="contactPerson" sx={{ mb: 1, fontWeight: 500 }}>
                    Contact Person<RequiredAsterisk>*</RequiredAsterisk>
                  </InputLabel>
                  <StyledTextField
                    fullWidth
                    id="contactPerson"
                    placeholder="Enter name of contact person"
                    {...register('contactPerson')}
                    error={!!errors.contactPerson}
                    variant="outlined"
                  />
                  {errors.contactPerson && <ErrorMessage>{errors.contactPerson.message}</ErrorMessage>}
                </Box>

                <Box sx={{ flex: 1 }}>
                  <InputLabel htmlFor="designation" sx={{ mb: 1, fontWeight: 500 }}>
                    Designation<RequiredAsterisk>*</RequiredAsterisk>
                  </InputLabel>
                  <StyledTextField
                    fullWidth
                    id="designation"
                    placeholder="Enter designation"
                    {...register('designation')}
                    error={!!errors.designation}
                    variant="outlined"
                  />
                  {errors.designation && <ErrorMessage>{errors.designation.message}</ErrorMessage>}
                </Box>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Box sx={{ flex: 1 }}>
                  <InputLabel htmlFor="phoneNumber" sx={{ mb: 1, fontWeight: 500 }}>
                    Phone Number<RequiredAsterisk>*</RequiredAsterisk>
                  </InputLabel>
                  <StyledTextField
                    fullWidth
                    id="phoneNumber"
                    type="tel"
                    placeholder="Enter facility's phone number"
                    {...register('phoneNumber')}
                    error={!!errors.phoneNumber}
                    variant="outlined"
                  />
                  {errors.phoneNumber && <ErrorMessage>{errors.phoneNumber.message}</ErrorMessage>}
                </Box>

                <Box sx={{ flex: 1 }}>
                  <InputLabel htmlFor="emailAddress" sx={{ mb: 1, fontWeight: 500 }}>
                    Email Address<RequiredAsterisk>*</RequiredAsterisk>
                  </InputLabel>
                  <StyledTextField
                    fullWidth
                    id="emailAddress"
                    type="email"
                    placeholder="Enter facility's email address"
                    {...register('emailAddress')}
                    error={!!errors.emailAddress}
                    variant="outlined"
                  />
                  {errors.emailAddress && <ErrorMessage>{errors.emailAddress.message}</ErrorMessage>}
                </Box>
              </Stack>

              <SubmitButton
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Next'}
              </SubmitButton>
            </FormBox>
          )}

          {activeStep === 1 && (
            <Box textAlign="center" py={6}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Verification Documents step content goes here
              </Typography>
              <Box mt={4}>
                <Button onClick={handleBack} sx={{ mr: 2 }}>
                  Back
                </Button>
                <Button variant="contained">Submit</Button>
              </Box>
            </Box>
          )}
        </FormContainer>
      </FormPanel>
    </PageContainer>
  );
};

export default FacilitySignupFirstPage;
